<template>
  <component
    :is="expandable ? 'UiExpandablePanel' : 'div'"
    :expanded="expandable ? panelExpanded : null"
    @toggle="expandable ? toggleExpandablePanel() : null"
  >
    <template #title>
      <div class="onboarding-wizard-sidebar__section--title">
        <h3>{{ $t('Account Setup') }}</h3>
        <span>{{ onboardingWizardStepsCompleted }} / {{ onboardingWizardStepsTotal }}</span>
      </div>
    </template>
    <template v-if="!expandable">
      <div class="onboarding-wizard-sidebar__section--title">
        <h3>{{ $t('Account Setup') }}</h3>
        <span>{{ onboardingWizardStepsCompleted }} / {{ onboardingWizardStepsTotal }}</span>
      </div>
    </template>
    <nav
      :aria-label="$t('Account Setup')"
      class="onboarding-wizard-sidebar__nav"
    >
      <ul class="onboarding-wizard-sidebar__ul">
        <li>
          <span class="onboarding-wizard-sidebar__item completed">
            <span class="onboarding-wizard-sidebar__text">
              <Fa
                :icon="$options.faRocket"
                class="ui-icon--small"
              />
              {{ $t('Create account') }}
            </span>
            <span class="onboarding-wizard-sidebar__icon">
              <Fa
                :icon="$options.faCircleCheck"
                class="ui-icon--medium"
              />
            </span>
          </span>
        </li>
        <li
          v-for="step in getPrimaryStepsOnboardingWizard"
          :id="step.id"
          :key="step.id"
        >
          <component
            :is="assignComponent(step).component"
            v-bind="assignComponent(step).props"
            :data-test="`onboarding-wizard-sidebar-list-item-${step.id}`"
            class="onboarding-wizard-sidebar__item"
            :class="[
              {
                completed: step.is_complete,
                disabled: isStepDisabled(step),
                active: isStepActive(step.id),
              },
            ]"
            v-on="assignComponent(step).handlers"
          >
            <span class="onboarding-wizard-sidebar__text">
              <Fa
                :icon="steps[step.id].icon"
                class="ui-icon--small"
              />
              {{ steps[step.id].title }}
            </span>
            <transition name="sc-fade">
              <span
                v-if="step.is_complete"
                class="onboarding-wizard-sidebar__icon"
              >
                <Fa
                  :icon="$options.faCircleCheck"
                  class="ui-icon--medium"
                />
              </span>
            </transition>
          </component>
          <UiTooltip
            v-if="step.is_complete && userIsInOWMandatoryDataCollectionExperimentTestGroup"
            :for="step.id"
            :text="$t('onboarding.tip_about_editing')"
          />
        </li>
      </ul>
    </nav>
  </component>
</template>

<script>
import { mapGetters } from 'vuex'
import Fa from 'vue-fa'
import {
  faRocket,
  faCircleCheck,
  faUser,
  faShop,
  faStar,
  faAddressCard,
  faEuroSign,
  faTruck,
} from '@fortawesome/free-solid-svg-icons'
import { UiExpandablePanel, UiTooltip } from '@sendcloud/ui-library'
import { ONBOARDING_WIZARD_SKIP } from '@/store/onboarding-wizard/action.types.js'
import DialogsService, { TYPE } from '@/services/dialogs.service.js'
import { ONBOARDING_WIZARD_ROUTE } from '@/router/routes/onboarding-wizard.routes'
import { SETTINGS_ACCOUNT_ROUTE } from '@/router/routes/settings/account-settings.routes.js'
import { SETTINGS_INTEGRATIONS_MANAGE_ROUTE } from '@/router/routes/settings/integrations.routes'
import { SETTINGS_ADDRESSES_SENDER_LIST_ROUTE } from '@/router/routes/settings/addresses.routes.js'
import { SETTINGS_FINANCIAL_PAYMENTS_ROUTE } from '@/router/routes/settings/financial.routes'
import { SETTINGS_CARRIERS_LIST_ROUTE } from '@/router/routes/settings/carriers-and-prices.routes'
import { SETTINGS_BRANDS_LIST_ROUTE } from '@/router/routes/settings/brands.routes.js'
import OnboardingAnalytics from '@/app/users/analytics/onboarding.analytics.js'

export default {
  name: 'OnboardingWizardSidebarList',

  faRocket,
  faCircleCheck,
  faUser,
  faShop,
  faStar,
  faAddressCard,
  faEuroSign,
  faTruck,
  ONBOARDING_WIZARD_ROUTE,

  components: {
    Fa,
    UiExpandablePanel,
    UiTooltip,
  },

  props: {
    expandable: {
      type: Boolean,
      required: false,
    },
  },

  emits: ['close-mobile-navigation'],

  data() {
    return {
      panelExpanded: false,
      steps: {
        'your-details': {
          title: this.$t('Your details'),
          icon: faUser,
          modalTitleObject: this.$t('onboarding.click_completed_step.modal.title.details'),
          modalContentObject: this.$t('onboarding.click_completed_step.modal.content.details'),
          modalBreadcrumbs: this.$t('onboarding.click_completed_step.modal.breadcrumb.details'),
          modalButtonObject: this.$t('onboarding.click_completed_step.modal.secondary_button_text.details'),
          parentPage: SETTINGS_ACCOUNT_ROUTE,
        },
        'integrations': {
          title: this.$t('Connect your store'),
          icon: faShop,
          modalTitleObject: this.$t('onboarding.click_completed_step.modal.title.store'),
          modalContentObject: this.$t('onboarding.click_completed_step.modal.content.store'),
          modalBreadcrumbs: this.$t('onboarding.click_completed_step.modal.breadcrumb.store'),
          modalButtonObject: this.$t('onboarding.click_completed_step.modal.secondary_button_text.store'),
          parentPage: SETTINGS_INTEGRATIONS_MANAGE_ROUTE,
        },
        'your-address': {
          title: this.$t('Add your address'),
          icon: faAddressCard,
          modalTitleObject: this.$t('onboarding.click_completed_step.modal.title.address'),
          modalContentObject: this.$t('onboarding.click_completed_step.modal.content.address'),
          modalBreadcrumbs: this.$t('onboarding.click_completed_step.modal.breadcrumb.address'),
          modalButtonObject: this.$t('onboarding.click_completed_step.modal.secondary_button_text.address'),
          parentPage: SETTINGS_ADDRESSES_SENDER_LIST_ROUTE,
        },
        'payment-method': {
          title: this.$t('Payment details'),
          icon: faEuroSign,
          modalTitleObject: this.$t('onboarding.click_completed_step.modal.title.payment_details'),
          modalContentObject: this.$t('onboarding.click_completed_step.modal.content.payment_details'),
          modalBreadcrumbs: this.$t('onboarding.click_completed_step.modal.breadcrumb.payment_details'),
          modalButtonObject: this.$t('onboarding.click_completed_step.modal.secondary_button_text.payment_details'),
          parentPage: SETTINGS_FINANCIAL_PAYMENTS_ROUTE,
        },
        'carriers': {
          title: this.$t('Enable carriers'),
          icon: faTruck,
          modalTitleObject: this.$t('onboarding.click_completed_step.modal.title.carriers'),
          modalContentObject: this.$t('onboarding.click_completed_step.modal.content.carriers'),
          modalBreadcrumbs: this.$t('onboarding.click_completed_step.modal.breadcrumb.carriers'),
          modalButtonObject: this.$t('onboarding.click_completed_step.modal.secondary_button_text.carriers'),
          parentPage: SETTINGS_CARRIERS_LIST_ROUTE,
        },
        'your-brand': {
          title: this.$t('Your brand'),
          icon: faStar,
          modalTitleObject: this.$t('onboarding.click_completed_step.modal.title.brand'),
          modalContentObject: this.$t('onboarding.click_completed_step.modal.content.brand'),
          modalBreadcrumbs: this.$t('onboarding.click_completed_step.modal.breadcrumb.brand'),
          modalButtonObject: this.$t('onboarding.click_completed_step.modal.secondary_button_text.brand'),
          parentPage: SETTINGS_BRANDS_LIST_ROUTE,
        },
      },
    }
  },

  computed: {
    ...mapGetters([
      'getPrimaryStepsOnboardingWizard',
      'onboardingWizardStepsTotal',
      'onboardingWizardStepsCompleted',
      'onboardingWizardMandatoryStepUncompleted',
      'userIsInOWMandatoryDataCollectionExperimentTestGroup',
    ]),

    stepId() {
      return this.$route.path.split('/onboarding/')[1] || []
    },

    isOnboardingRoute() {
      return this.$route.matched.some(route => route.name === ONBOARDING_WIZARD_ROUTE)
    },
  },

  methods: {
    assignComponent(step) {
      if (!step.is_complete && !this.isStepActive(step.id) && !this.onboardingWizardMandatoryStepUncompleted &&
        !this.userIsInOWMandatoryDataCollectionExperimentTestGroup) {
        return {
          component: 'router-link',
          props: {
            to: { name: ONBOARDING_WIZARD_ROUTE + '.' + step.id },
          },
          handlers: {
            click: () => this.toggleSidebar(step.id),
          },
        }
      } else if (step.is_complete && this.isOnboardingRoute &&
      !this.userIsInOWMandatoryDataCollectionExperimentTestGroup) {
        return {
          component: 'button',
          props: {},
          handlers: {
            click: () => this.showCompletedStepModal(step.id),
          },
        }
      } else {
        return {
          component: 'span',
          props: {},
          handlers: {},
        }
      }
    },

    toggleSidebar() {
      this.$emit('close-mobile-navigation')
    },

    toggleExpandablePanel() {
      // referring to PointerEvent because UiExpandablePanel doesn't pass event payload
      event.stopPropagation()
      this.panelExpanded = !this.panelExpanded
    },

    async showCompletedStepModal(stepId) {
      const parentPagePath = this.$router.getRoutes().find(route => route.name === this.steps[stepId].parentPage)?.path

      const content = document.createElement('div')
      content.innerHTML = this.$t('onboarding.click_completed_step.modal.content', {
        insertContent: this.steps[stepId].modalContentObject,
        insertBreadcrumb: '<b>' + this.steps[stepId].modalBreadcrumbs + '</b>',
      })

      const dialogWasConfirmed = await DialogsService.alert({
        className: 'onboarding-wizard-completed-step__modal',
        icon: TYPE.INFO,
        dangerMode: true,
        title: this.$t('onboarding.click_completed_step.modal.title', {
          insertTitle: this.steps[stepId].modalTitleObject,
        }),
        content,
        buttons: {
          continue: {
            value: false,
            text: this.$t('onboarding.click_completed_step.modal.primary_button_text'),
            className: 'ui-button ui-button--primary',
          },
          leave: {
            value: true,
            text: this.$t('onboarding.click_completed_step.modal.secondary_button_text', {
              buttonText: this.steps[stepId].modalButtonObject,
            }),
            className: 'ui-button ui-button--secondary',
          },
        },
      })

      if (dialogWasConfirmed) {
        OnboardingAnalytics.onboardingWizardExitedThroughModal(parentPagePath)
        await this.$store.dispatch(ONBOARDING_WIZARD_SKIP)
        this.$router.push({ name: this.steps[stepId].parentPage })
      }
    },

    isStepActive(stepId) {
      return this.stepId.includes(stepId)
    },

    /**
     * The step has to be disabled if the mandatory step is uncompleted,
     * or in the experiment, any step other than the current step cannot be clicked.
     * TODO: delete the second condition after the MANDATORY_DATA_COLLECTION_OW_EXPERIMENT_ID experiment is over
     */
    isStepDisabled(step) {
      return (this.onboardingWizardMandatoryStepUncompleted && step.id !== 'your-details') ||
        (this.userIsInOWMandatoryDataCollectionExperimentTestGroup && !this.isStepActive(step.id) && !step.is_complete)
    },
  },
}
</script>

<style lang="scss" scoped>
.onboarding-wizard-sidebar__section--title {
  padding: $sc-spacing-8 $sc-spacing-8 $sc-spacing-8 $sc-spacing-12;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;

  h3 {
    padding: 0;
    margin: 0;
    font-size: 1.125rem;
  }

  span {
    font-size: 1.125rem;
  }
}

.onboarding-wizard-sidebar__nav {
  padding: 0;
  margin: 0;
}

.onboarding-wizard-sidebar__ul {
  padding: 0;
  margin: 0;
  list-style: none;
  width: 100%;
}

.onboarding-wizard-sidebar__text {
  display: flex;
  align-items: center;
  transition: text-decoration 200ms;
  color: $sc-dark-blue;

  svg {
    margin-right: $sc-spacing-12;
  }
}

.onboarding-wizard-sidebar__item {
  display: flex;
  width: 100%;
  justify-content: space-between;
  padding: $sc-spacing-12 $sc-spacing-8 $sc-spacing-12 $sc-spacing-12;
  border-radius: $sc-border-radius;
  border: none;
  text-decoration: none;
  background-color: transparent;

  &.active {
    font-weight: 600;
    background-color: $sc-informative-10;
  }

  &:hover {
    background: color.mix($sc-informative, #fff, 5%);
  }

  &.completed,
  &.disabled {
    .onboarding-wizard-sidebar__text {
      color: $sc-dark-blue-75;
    }
  }

  &.disabled {
    .onboarding-wizard-sidebar__text {
      opacity: 0.75;
    }
  }

  &.completed {
    .onboarding-wizard-sidebar__text {
      text-decoration: line-through;
    }
  }

}

.onboarding-wizard-sidebar__icon {
  display: inline-flex;

  svg {
    color: $sc-positive;
  }
}
</style>

<style lang="scss">
// overwrite the styles coming from UiExpandablePanel
.onboarding-wizard-dropdown .ui-panel {
  border: none;
}

.onboarding-wizard-dropdown .ui-expandable-panel__toggle,
.onboarding-wizard-dropdown .ui-expandable-panel__body {
  padding: 0;
}

.onboarding-wizard-dropdown .ui-expandable-panel__toggle {
  font-weight: inherit;
}

.onboarding-wizard-dropdown .ui-expandable-panel__toggle:hover {
  text-decoration: none;
}

.onboarding-wizard-completed-step__modal {
  width: 600px;
}
</style>
